import React, { useMemo, useState, useEffect } from "react";
import styles from "./style.module.scss";
import { useSelector } from "react-redux";
import { RootState } from "../../../state";
import { isEqual } from "lodash";
import {
  getPreferredFormattedTimeMs,
  formatTimestampWithMs,
} from "../../../utils/preferences";
import { FlagPeriod } from "../../../types/interventions";

const FLAG_LABELS: Record<string, string> = {
  RED: "Red Flag",
  FULL_YELLOW: "Full Course Yellow",
  SAFETY_CAR: "Safety Car",
  GREEN: "Green Flag",
  FINISH: "Chequered Flag",
};
const FLAG_STYLES: Record<string, string> = {
  RED: styles.red,
  FULL_YELLOW: styles.yellow,
  SAFETY_CAR: styles.orange,
  GREEN: styles.green,
  FINISH: styles.chequered,
};

function SessionFlags() {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 100);
    return () => clearInterval(id);
  }, []);

  const sessionStatus = useSelector(
    (state: RootState) => state.aks.v2.timing?.session?.status,
    isEqual,
  );
  const flagPeriods = useSelector(
    (state: RootState) => state.interventions.flagPeriods,
    isEqual,
  );

  const stoppedTime = getPreferredFormattedTimeMs(
    sessionStatus?.stoppedMilliSeconds || 0,
  );

  const totals = useMemo(() => {
    let fcy = 0,
      sc = 0;
    for (const p of flagPeriods) {
      if (p.type !== "FULL_YELLOW" && p.type !== "SAFETY_CAR") continue;
      const dur = p.endTime ? p.endTime - p.startTime : now - p.startTime;
      if (p.type === "FULL_YELLOW") fcy += dur;
      else sc += dur;
    }
    return {
      fcy: getPreferredFormattedTimeMs(fcy),
      sc: getPreferredFormattedTimeMs(sc),
      total: getPreferredFormattedTimeMs(fcy + sc),
    };
  }, [flagPeriods, now]);

  const getDuration = (p: FlagPeriod) => {
    if (p.type === "FINISH") return "-";
    const dur = p.endTime ? p.endTime - p.startTime : now - p.startTime;
    return getPreferredFormattedTimeMs(dur);
  };

  return (
    <div className={styles.sessionFlags}>
      <table>
        <thead>
          <tr className={styles.titleRow}>
            <th colSpan={4}>Interventions</th>
          </tr>
          <tr>
            <th>Type</th>
            <th>Start Time</th>
            <th>End Time</th>
            <th>Duration</th>
          </tr>
        </thead>
        <tbody>
          {flagPeriods.map((p, i) => (
            <tr key={i} className={FLAG_STYLES[p.type]}>
              <td>{FLAG_LABELS[p.type]}</td>
              <td>{formatTimestampWithMs(p.startTime)}</td>
              <td>{formatTimestampWithMs(p.endTime)}</td>
              <td>{getDuration(p)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className={styles.statsRow}>
            <td>
              Stopped: <strong>{stoppedTime}</strong>
            </td>
            <td>
              FCY: <strong>{totals.fcy}</strong>
            </td>
            <td>
              SC: <strong>{totals.sc}</strong>
            </td>
            <td>
              Total: <strong>{totals.total}</strong>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

export default SessionFlags;
